import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  id?: string;
  touchFriendly?: boolean;
  date?: Date;
  onChange?: (date?: Date) => void;
  disabled?: (date: Date) => boolean;
  className?: string;
  disablePicker?: boolean;
  dateFormat?: string;
}

export function DatePicker({
  id,
  touchFriendly = false,
  date,
  onChange,
  disabled,
  className,
  disablePicker,
  dateFormat = "PPP",
}: DatePickerProps) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-gray-500",
            "sm:w-auto",
            className,
          )}
          disabled={disablePicker}
        >
          <CareIcon icon="l-calender" className="mr-0 size-4 shrink-0" />
          <span className="truncate">
            {date ? (
              <>
                <span className="block sm:hidden">
                  {format(date, "MMM d, yyyy")}
                </span>
                <span className="hidden sm:block">
                  {format(date, dateFormat)}
                </span>
              </>
            ) : (
              <span>{t("pick_a_date")}</span>
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={
          touchFriendly
            ? "w-auto max-w-[calc(100vw-1rem)] p-1"
            : "w-auto p-0 sm:w-64"
        }
        align="start"
      >
        <Calendar
          dayClassName={touchFriendly ? "size-10 sm:size-11" : undefined}
          dayButtonClassName={touchFriendly ? "size-10 sm:size-11" : undefined}
          weekdayClassName={touchFriendly ? "w-10 sm:w-11" : undefined}
          buttonNextClassName={touchFriendly ? "size-10" : undefined}
          buttonPreviousClassName={touchFriendly ? "size-10" : undefined}
          monthCaptionClassName={touchFriendly ? "h-10" : undefined}
          mode="single"
          selected={date}
          onSelect={(date) => {
            onChange?.(date);
            setOpen(false);
          }}
          captionLayout="dropdown"
          endMonth={new Date(2100, 11, 31)}
          autoFocus
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}
